import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { useId, useState, type KeyboardEvent, type ReactNode } from "react";
import { enterTransition, exitTransition } from "@/lib/motion";
import { cn } from "@/lib/utils";
import { GlideTrack } from "./glide-highlight";

export type TabItem = {
  value: string;
  label: string;
  content: ReactNode;
};

export function Tabs({
  items,
  value,
  defaultValue,
  onValueChange,
  label,
  className,
  panelClassName,
}: {
  items: TabItem[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  label: string;
  className?: string;
  /** Replaces the panel's own card look, for tabs that switch whole sections of a page. */
  panelClassName?: string;
}) {
  const uid = useId();
  const first = items[0]?.value ?? "";
  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? first);
  const selected = value ?? uncontrolled;
  const reduce = useReducedMotion();
  const current = items.find((item) => item.value === selected) ?? items[0];

  function select(next: string) {
    if (value === undefined) setUncontrolled(next);
    onValueChange?.(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = items.length - 1;
    let next = index;
    if (event.key === "ArrowRight") next = index === last ? 0 : index + 1;
    else if (event.key === "ArrowLeft") next = index === 0 ? last : index - 1;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = last;
    else return;
    event.preventDefault();
    const item = items[next];
    if (!item) return;
    select(item.value);
    event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>("[role='tab']")[next]?.focus();
  }

  return (
    <div className={cn("grid gap-4", className)} data-slot="tabs">
      <GlideTrack
        activeKey={selected}
        className="inline-flex w-fit max-w-full gap-1 overflow-x-auto rounded-pill bg-surface-muted p-1"
      >
        {(register) => (
          <div role="tablist" aria-label={label} className="inline-flex gap-1">
            {items.map((item, index) => {
              const active = item.value === selected;
              return (
                <button
                  key={item.value}
                  ref={register(item.value)}
                  role="tab"
                  type="button"
                  id={`${uid}-tab-${item.value}`}
                  aria-selected={active}
                  aria-controls={active ? `${uid}-panel` : undefined}
                  tabIndex={active ? 0 : -1}
                  className={cn(
                    "relative z-10 rounded-pill px-4 py-2 text-sm",
                    active ? "text-foreground" : "text-secondary",
                  )}
                  onClick={() => select(item.value)}
                  onKeyDown={(event) => onKeyDown(event, index)}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </GlideTrack>
      {/* One panel that stays, so the selected tab's aria-controls always names it; its content fades between tabs. */}
      <div
        role="tabpanel"
        id={`${uid}-panel`}
        aria-labelledby={current ? `${uid}-tab-${current.value}` : undefined}
        tabIndex={0}
        className={panelClassName ?? "rounded-surface border border-line-subtle bg-surface px-5 py-4 text-sm text-secondary outline-none"}
      >
        <AnimatePresence mode="wait" initial={false}>
          {current ? (
            <motion.div
              key={current.value}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={
                reduce
                  ? { opacity: 0, transition: exitTransition(true) }
                  : { opacity: 0, y: -4, transition: exitTransition(false) }
              }
              transition={enterTransition(reduce)}
            >
              {current.content}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}
